import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, ErrorState, Field, FormError, Input, LoadingState, PageHeader, Status, formatRelative } from '@lawbee/ui';
import { BellRing, Check, Play } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PlanGate } from '../../../components/PlanGate';
import { api, errorMessage } from '../../../lib/api';
import { useApiMutation } from '../../../lib/hooks';
import { can, ME_KEY, useMe } from '../../../lib/me';
import type { CheckinFlag, CheckinSettings } from '../../../lib/types';
import { ReadOnlyNote, Switch } from './shared';

type CheckinsResponse = { flags: CheckinFlag[]; settings: CheckinSettings };

const KIND_GROUPS: { kind: CheckinFlag['kind']; title: string; tone: 'blocked' | 'risk' | 'idle' }[] = [
  { kind: 'deadline', title: 'Upcoming or missed deadlines', tone: 'blocked' },
  { kind: 'document', title: 'Overdue document requests', tone: 'risk' },
  { kind: 'inactive', title: 'Inactive matters', tone: 'idle' },
];

export function CheckinsPage() {
  return (
    <PlanGate feature="deadline_checkins">
      <CheckinsInner />
    </PlanGate>
  );
}

function CheckinsInner() {
  const me = useMe();
  const q = useQuery({ queryKey: ['checkins'], queryFn: () => api.get<CheckinsResponse>('/api/checkins') });
  const run = useApiMutation(() => api.post<{ created: number }>('/api/checkins/run'), {
    success: (d) => `${d.created} new ${d.created === 1 ? 'flag' : 'flags'}`,
    invalidate: [['checkins'], ['dashboard']],
  });

  return (
    <div>
      <PageHeader
        title="Check-ins"
        description="LawBee watches open matters for approaching deadlines, overdue document requests and matters that have gone quiet."
        actions={
          can(me, 'client_work') ? (
            <Button onClick={() => run.mutate(undefined)} loading={run.isPending} size="sm">
              <Play /> Run now
            </Button>
          ) : null
        }
      />
      {q.isLoading ? <LoadingState label="Loading check-ins" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {q.data.flags.length === 0 ? (
              <Card>
                <EmptyState icon={<BellRing />} title="All clear" description="No open flags. Check-ins run every day; use Run now to check immediately." />
              </Card>
            ) : (
              KIND_GROUPS.map((g) => {
                const flags = q.data.flags.filter((f) => f.kind === g.kind);
                if (flags.length === 0) return null;
                return (
                  <section key={g.kind} aria-labelledby={`flags-${g.kind}`}>
                    <h2 id={`flags-${g.kind}`} className="mb-2 flex items-center gap-2 text-sm font-semibold">
                      <Status tone={g.tone}>{g.title}</Status>
                      <Badge>
                        <span className="figures">{flags.length}</span>
                      </Badge>
                    </h2>
                    <Card>
                      <ul className="divide-y divide-border/70">
                        {flags.map((f) => (
                          <FlagRow key={f.id} flag={f} canResolve={can(me, 'client_work')} />
                        ))}
                      </ul>
                    </Card>
                  </section>
                );
              })
            )}
          </div>
          <CheckinSettingsCard settings={q.data.settings} canEdit={can(me, 'settings') || can(me, 'approve_updates')} />
        </div>
      ) : null}
    </div>
  );
}

function FlagRow({ flag: f, canResolve }: { flag: CheckinFlag; canResolve: boolean }) {
  const resolve = useApiMutation(() => api.post(`/api/checkins/${f.id}/resolve`), { success: 'Flag resolved', invalidate: [['checkins'], ['dashboard']] });
  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
      <div className="min-w-0 flex-1 space-y-1">
        <Link to="/matters/$id" params={{ id: f.matterId }} className="block truncate text-sm font-medium hover:underline">
          {f.matterNumber ? <span className="figures mr-1.5">{f.matterNumber}</span> : null}
          {f.matterTitle ?? 'Matter'}
        </Link>
        <p className="text-[13px] text-muted-foreground">{f.message}</p>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            Flagged <span className="figures">{formatRelative(f.createdAt)}</span>
          </span>
          {f.clientNudgeSentAt ? (
            <span className="inline-flex items-center gap-1 text-success">
              <Check className="size-3" aria-hidden /> Client nudged <span className="figures">{formatRelative(f.clientNudgeSentAt)}</span>
            </span>
          ) : null}
        </p>
      </div>
      {canResolve ? (
        <Button variant="outline" size="sm" className="self-start sm:self-center" loading={resolve.isPending} onClick={() => resolve.mutate(undefined)}>
          Resolve
        </Button>
      ) : null}
    </li>
  );
}

function CheckinSettingsCard({ settings, canEdit }: { settings: CheckinSettings; canEdit: boolean }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ deadlineWindowDays: String(settings.deadlineWindowDays), inactivityDays: String(settings.inactivityDays), documentGraceDays: String(settings.documentGraceDays) });
  const [clientNudges, setClientNudges] = useState(settings.clientNudges);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm({ deadlineWindowDays: String(settings.deadlineWindowDays), inactivityDays: String(settings.inactivityDays), documentGraceDays: String(settings.documentGraceDays) });
    setClientNudges(settings.clientNudges);
  }, [settings]);

  const save = useApiMutation((checkins: CheckinSettings) => api.patch('/api/workspace/settings', { settings: { checkins } }), {
    success: 'Check-in settings saved',
    invalidate: [['checkins'], ['workspace-settings']],
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const d = Number(form.deadlineWindowDays);
    const i = Number(form.inactivityDays);
    const g = Number(form.documentGraceDays);
    if (!Number.isInteger(d) || d < 1 || d > 60) return setError('Deadline window must be 1 to 60 days.');
    if (!Number.isInteger(i) || i < 3 || i > 365) return setError('Inactivity must be 3 to 365 days.');
    if (!Number.isInteger(g) || g < 1 || g > 60) return setError('Document grace period must be 1 to 60 days.');
    setError(null);
    save.mutate({ deadlineWindowDays: d, inactivityDays: i, documentGraceDays: g, clientNudges }, { onError: (err) => setError(errorMessage(err)) });
  };

  const field = (key: keyof typeof form, label: string, description: string) => (
    <Field label={label} htmlFor={`ci-${key}`} description={description}>
      <Input
        id={`ci-${key}`}
        type="number"
        inputMode="numeric"
        min={1}
        className="figures"
        value={form[key]}
        disabled={!canEdit}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      />
    </Field>
  );

  return (
    <Card className="self-start">
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>When LawBee raises a flag.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {field('deadlineWindowDays', 'Deadline window (days)', 'Flag deadlines due within this many days, and any missed.')}
          {field('inactivityDays', 'Inactivity (days)', 'Flag open matters with no activity for this long.')}
          {field('documentGraceDays', 'Document grace (days)', 'Flag document requests still open this long after their due date.')}
          <Switch
            checked={clientNudges}
            onCheckedChange={setClientNudges}
            disabled={!canEdit}
            label="Client nudges"
            description="Email the client a gentle reminder for overdue document requests."
          />
          <FormError message={error} />
          {canEdit ? (
            <Button type="submit" className="w-full" loading={save.isPending}>
              Save settings
            </Button>
          ) : (
            <ReadOnlyNote>Only an owner or lawyer can change check-in settings.</ReadOnlyNote>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
